import { useEffect, useRef, useState } from 'react';
import type { TarefaDeProjeto } from '@/features/projetos/projetos.api';

// ── constantes de layout ──────────────────────────────────────────────────────

const ROW_H = 36;          // px por linha
const LABEL_W = 280;       // px do painel esquerdo
const MIN_PX_PER_DAY = 40; // px por dia (zoom)
const HEADER_H = 48;       // px do cabeçalho de tempo

// Epoch em minutos → ms
const toMs = (epochMin: number) => epochMin * 60_000;

// ── utilitários de tempo ──────────────────────────────────────────────────────

/**
 * Retorna o instante atual em minutos de época (sem usar Date.now() inline
 * em posições calculadas; aqui só para a linha "hoje").
 */
function nowMinutes(): number {
  return Math.floor(Date.now() / 60_000);
}

/** Arredonda para o início do dia (UTC). */
function startOfDay(epochMin: number): number {
  const ms = toMs(epochMin);
  const d = new Date(ms);
  d.setUTCHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 60_000);
}

/** Arredonda para o início do mês (UTC). */
function startOfMonth(epochMin: number): number {
  const d = new Date(toMs(epochMin));
  d.setUTCDate(1);
  d.setUTCHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 60_000);
}

/** Próximo mês em minutos de época. */
function nextMonth(epochMin: number): number {
  const d = new Date(toMs(epochMin));
  d.setUTCMonth(d.getUTCMonth() + 1, 1);
  d.setUTCHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 60_000);
}

const MESES_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function fmtDia(epochMin: number): string {
  const d = new Date(epochMin * 60_000);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

function monthLabel(epochMin: number): string {
  const d = new Date(toMs(epochMin));
  return `${MESES_PT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Semanas completas entre dois pontos (domingo→sábado). */
function weekStarts(from: number, to: number): number[] {
  const DAY = 1440; // minutos
  const starts: number[] = [];
  let cur = startOfDay(from);
  // avança até domingo
  const d = new Date(toMs(cur));
  const dow = d.getUTCDay(); // 0=dom
  if (dow !== 0) cur -= dow * DAY;
  while (cur < to) {
    if (cur >= from) starts.push(cur);
    cur += 7 * DAY;
  }
  return starts;
}

// ── componente ────────────────────────────────────────────────────────────────

interface Props {
  tarefas: TarefaDeProjeto[];
  /** Proporção de pixels por dia útil (20 = padrão, 40 = zoom in). */
  pxPerDay?: number;
}

export function GanttChart({ tarefas, pxPerDay = MIN_PX_PER_DAY }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(800);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (e) setContainerW(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Filtra tarefas com datas computadas
  const comDatas = tarefas.filter((t) => t.startAt != null && t.finishAt != null);

  if (comDatas.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        Nenhuma tarefa com cronograma calculado.
        <br />
        Adicione tarefas com duração para gerar o Gantt.
      </div>
    );
  }

  // Intervalo do projeto
  const projectStart = Math.min(...comDatas.map((t) => t.startAt!));
  const projectFinish = Math.max(...comDatas.map((t) => t.finishAt!));

  // Auto-fit: se o projeto for longo, reduz o zoom para caber no container
  const totalDays = (projectFinish - projectStart) / 1440;
  const effectivePxPerDay = totalDays > 0 && totalDays * pxPerDay > containerW - 20
    ? Math.max(6, (containerW - 20) / totalDays)
    : pxPerDay;

  const pxPerMin = effectivePxPerDay / 1440; // px por minuto
  const totalWidth = (projectFinish - projectStart) * pxPerMin;
  const svgWidth = Math.max(totalWidth, 400);

  const today = nowMinutes();
  const todayX = (today - projectStart) * pxPerMin;
  const showToday = today >= projectStart && today <= projectFinish;

  // Meses e semanas para o cabeçalho
  const months: Array<{ start: number; label: string }> = [];
  let mCur = startOfMonth(projectStart);
  while (mCur < projectFinish) {
    months.push({ start: mCur, label: monthLabel(mCur) });
    mCur = nextMonth(mCur);
  }

  const weeks = weekStarts(projectStart, projectFinish);

  const svgH = comDatas.length * ROW_H;

  function taskX(t: TarefaDeProjeto): number {
    return (t.startAt! - projectStart) * pxPerMin;
  }

  function taskW(t: TarefaDeProjeto): number {
    return Math.max((t.finishAt! - t.startAt!) * pxPerMin, 4);
  }

  // ── setas de dependência ────────────────────────────────────────────────────

  const DIAMOND_R = 7; // raio do losango de marco (deve bater com o <polygon>)
  const ELBOW = 10;    // offset horizontal antes/depois da linha vertical
  const ARW = 5;       // espaço reservado para a ponta da seta

  // Índice de linha por taskId (somente tarefas com datas)
  const rowIndex = new Map<string, number>(comDatas.map((t, i) => [t.id, i]));

  // Dependências únicas cujas duas pontas têm datas calculadas
  const deps = Array.from(
    new Map(comDatas.flatMap((t) => t.successors).map((d) => [d.id, d])).values()
  ).filter((d) => rowIndex.has(d.predecessorId) && rowIndex.has(d.successorId));

  /** Ponto de ancoragem X de uma tarefa num lado (start ou finish). */
  function depAnchor(task: TarefaDeProjeto, side: 'start' | 'finish'): number {
    if (task.isMilestone) {
      const cx = (task.finishAt! - projectStart) * pxPerMin;
      return side === 'start' ? cx - DIAMOND_R : cx + DIAMOND_R;
    }
    return side === 'start' ? taskX(task) : taskX(task) + taskW(task);
  }

  /** Caminho SVG ortogonal entre predecessor e successor. */
  function depLinePath(
    predTask: TarefaDeProjeto, predRow: number,
    succTask: TarefaDeProjeto, succRow: number,
    kind: 'FS' | 'SS' | 'FF' | 'SF',
  ): string {
    const cy1 = HEADER_H + predRow * ROW_H + ROW_H / 2;
    const cy2 = HEADER_H + succRow * ROW_H + ROW_H / 2;

    const sx = depAnchor(predTask, kind === 'SS' || kind === 'SF' ? 'start' : 'finish');
    const tx = depAnchor(succTask, kind === 'FS' || kind === 'SS' ? 'start' : 'finish');

    if (kind === 'FS' || kind === 'SS') {
      // Chega pelo lado esquerdo do successor → último segmento vai para direita
      const atx = tx - ARW;
      if (atx >= sx + 2) {
        // Caminho normal em L
        const mx = sx + ELBOW;
        return `M${sx},${cy1} H${mx} V${cy2} H${atx}`;
      }
      // Backward: contorna pela esquerda
      const dir = predRow <= succRow ? 1 : -1;
      const leftX = Math.min(sx - ELBOW, tx - ELBOW * 2);
      return `M${sx},${cy1} H${sx + ELBOW} V${cy1 + dir * ROW_H * 0.4} H${leftX} V${cy2} H${atx}`;
    }

    // FF / SF: chega pelo lado direito → último segmento vai para esquerda
    const atx = tx + ARW;
    const rightX = Math.max(sx + ELBOW, tx + ELBOW);
    return `M${sx},${cy1} H${rightX} V${cy2} H${atx}`;
  }

  const nowMin = nowMinutes();
  const isDelayed = (t: TarefaDeProjeto) =>
    !t.isMilestone && t.progress < 100 && t.finishAt !== null && t.finishAt < nowMin;

  return (
    <div className="flex w-full select-none overflow-hidden rounded-lg border border-border">
      {/* Painel esquerdo: nomes */}
      <div
        className="shrink-0 border-r border-border bg-background"
        style={{ width: LABEL_W }}
      >
        {/* cabeçalho vazio alinhado */}
        <div
          className="border-b border-border bg-muted/30"
          style={{ height: HEADER_H }}
        />
        {comDatas.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-1.5 border-b border-border/50 px-3"
            style={{
              height: ROW_H,
              paddingLeft: `${t.depth * 12 + 12}px`,
            }}
          >
            {t.isMilestone && (
              <span className="shrink-0 text-xs text-primary" aria-hidden>◆</span>
            )}
            <span
              className={`truncate text-xs font-medium ${
                t.isMilestone ? 'italic text-primary/80'
                : isDelayed(t) ? 'text-destructive'
                : t.isCritical ? 'text-cyan-700 dark:text-cyan-400'
                : 'text-foreground'
              }`}
            >
              {t.outlineNumber} {t.name}
            </span>
            {!t.isMilestone && (t.isCritical || isDelayed(t)) && (
              <span className={`ml-auto shrink-0 h-1.5 w-1.5 rounded-full ${isDelayed(t) ? 'bg-destructive' : 'bg-cyan-700 dark:bg-cyan-400'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Painel direito: SVG Gantt com scroll horizontal */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-x-auto"
        style={{ cursor: 'default' }}
      >
        <svg
          width={svgWidth}
          height={HEADER_H + svgH}
          style={{ display: 'block' }}
          aria-label="Gantt chart"
        >
          <defs>
            {/* Seta normal */}
            <marker id="dep-n" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0,0 7,3.5 0,7" fill="#94a3b8" opacity="0.9" />
            </marker>
            {/* Seta crítica */}
            <marker id="dep-c" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0,0 7,3.5 0,7" fill="#2BBFCF" opacity="0.95" />
            </marker>
          </defs>

          {/* ── Cabeçalho de tempo ── */}
          <g>
            {/* Fundo do cabeçalho */}
            <rect
              x={0}
              y={0}
              width={svgWidth}
              height={HEADER_H}
              className="fill-muted/30"
            />
            {/* Linha meses */}
            {months.map((m) => {
              const x = (m.start - projectStart) * pxPerMin;
              const nextX =
                months[months.findIndex((mm) => mm.start === m.start) + 1]?.start;
              const w = nextX
                ? (nextX - m.start) * pxPerMin
                : svgWidth - x;
              return (
                <g key={m.start}>
                  <rect
                    x={x}
                    y={0}
                    width={w}
                    height={24}
                    className="fill-transparent"
                    stroke="currentColor"
                    strokeOpacity={0.1}
                    strokeWidth={1}
                  />
                  <text
                    x={x + 4}
                    y={16}
                    className="fill-muted-foreground text-[10px] font-semibold"
                    style={{ fontSize: 10 }}
                  >
                    {m.label}
                  </text>
                </g>
              );
            })}
            {/* Linha semanas */}
            {weeks.map((ws) => {
              const x = (ws - projectStart) * pxPerMin;
              const d = new Date(toMs(ws));
              const label = `${String(d.getUTCDate()).padStart(2, '0')}`;
              return (
                <g key={ws}>
                  <line
                    x1={x}
                    y1={24}
                    x2={x}
                    y2={HEADER_H}
                    stroke="currentColor"
                    strokeOpacity={0.12}
                    strokeWidth={1}
                  />
                  <text
                    x={x + 2}
                    y={HEADER_H - 6}
                    className="fill-muted-foreground"
                    style={{ fontSize: 9 }}
                  >
                    {label}
                  </text>
                </g>
              );
            })}
          </g>

          {/* ── Linhas de grade vertical (semanas) ── */}
          <g opacity={0.06}>
            {weeks.map((ws) => (
              <line
                key={ws}
                x1={(ws - projectStart) * pxPerMin}
                y1={HEADER_H}
                x2={(ws - projectStart) * pxPerMin}
                y2={HEADER_H + svgH}
                stroke="currentColor"
                strokeWidth={1}
              />
            ))}
          </g>

          {/* ── Linhas de grade horizontal ── */}
          <g opacity={0.06}>
            {comDatas.map((_, i) => (
              <line
                key={i}
                x1={0}
                y1={HEADER_H + i * ROW_H}
                x2={svgWidth}
                y2={HEADER_H + i * ROW_H}
                stroke="currentColor"
                strokeWidth={1}
              />
            ))}
          </g>

          {/* ── Linhas de dependência ── */}
          <g>
            {deps.map((dep) => {
              const predTask = comDatas.find((t) => t.id === dep.predecessorId)!;
              const succTask = comDatas.find((t) => t.id === dep.successorId)!;
              const predRow = rowIndex.get(dep.predecessorId)!;
              const succRow = rowIndex.get(dep.successorId)!;
              const driving = succTask.drivingLinkId === dep.id;
              const stroke = driving ? '#2BBFCF' : '#94a3b8';
              const opacity = driving ? 0.75 : 0.45;
              const sw = driving ? 1.5 : 1;
              const markerId = driving ? 'dep-c' : 'dep-n';
              const d = depLinePath(predTask, predRow, succTask, succRow, dep.kind);
              return (
                <path
                  key={dep.id}
                  d={d}
                  fill="none"
                  stroke={stroke}
                  strokeWidth={sw}
                  strokeOpacity={opacity}
                  markerEnd={`url(#${markerId})`}
                />
              );
            })}
          </g>

          {/* ── Barras de tarefas ── */}
          {comDatas.map((t, i) => {
            const cy = HEADER_H + i * ROW_H + ROW_H / 2;

            if (t.isMilestone) {
              const cx = (t.finishAt! - projectStart) * pxPerMin;
              const r = 7;
              const pts = `${cx},${cy - r} ${cx + r},${cy} ${cx},${cy + r} ${cx - r},${cy}`;
              const fill = t.isCritical ? '#2BBFCF' : '#94a3b8';
              return (
                <g key={t.id}>
                  <polygon points={pts} fill={fill} fillOpacity={0.9} />
                  <polygon points={pts} fill="none" stroke={fill} strokeWidth={1.5} strokeOpacity={1} />
                  <text x={cx + r + 4} y={cy + 4} style={{ fontSize: 9, fill: fill }}>{fmtDia(t.finishAt!)}</text>
                </g>
              );
            }

            const x = taskX(t);
            const w = taskW(t);
            const y = HEADER_H + i * ROW_H + 7;
            const barH = ROW_H - 14;
            const progressW = (t.progress / 100) * w;

            const delayed = isDelayed(t);
            const barFill = delayed ? '#dc2626' : t.isCritical ? '#2BBFCF' : '#94a3b8';
            const progressFill = delayed ? '#991b1b' : t.isCritical ? '#1a8e9e' : '#64748b';
            const labelX = x + w + 4;
            const labelColor = delayed ? '#dc2626' : t.isCritical ? '#1a8e9e' : '#64748b';

            return (
              <g key={t.id}>
                {/* Barra de fundo */}
                <rect x={x} y={y} width={w} height={barH} rx={3} fill={barFill} fillOpacity={0.2} />
                {/* Progresso */}
                {progressW > 0 && (
                  <rect x={x} y={y} width={progressW} height={barH} rx={3} fill={progressFill} fillOpacity={0.6} />
                )}
                {/* Borda */}
                <rect x={x} y={y} width={w} height={barH} rx={3} fill="none" stroke={barFill} strokeOpacity={0.8} strokeWidth={1} />
                {/* Data de início à esquerda da barra */}
                <text x={x} y={y - 2} style={{ fontSize: 9, fill: labelColor, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtDia(t.startAt!)}
                </text>
                {/* Data de término à direita */}
                <text x={labelX} y={cy + 4} style={{ fontSize: 9, fill: '#888' }}>
                  {fmtDia(t.finishAt! - 1)}
                </text>
              </g>
            );
          })}

          {/* ── Linha do hoje ── */}
          {showToday && (
            <g>
              <line
                x1={todayX}
                y1={0}
                x2={todayX}
                y2={HEADER_H + svgH}
                stroke="#f59e0b"
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
              <circle cx={todayX} cy={HEADER_H} r={4} fill="#f59e0b" />
            </g>
          )}
        </svg>
      </div>
    </div>
  );
}
